/**
 * Password validation utilities
 * Rules: min 8 chars, uppercase, lowercase, number, special character
 * Matches backend Laravel Password::min(8)->letters()->mixedCase()->numbers()->symbols()
 */

export interface PasswordCheck {
  label: string;
  met: boolean;
}

export function getPasswordChecks(password: string): PasswordCheck[] {
  return [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One uppercase letter (A-Z)', met: /[A-Z]/.test(password) },
    { label: 'One lowercase letter (a-z)', met: /[a-z]/.test(password) },
    { label: 'One number (0-9)', met: /[0-9]/.test(password) },
    { label: 'One special character (!@#$%^&* etc)', met: /[^A-Za-z0-9]/.test(password) },
  ];
}

export function isPasswordStrong(password: string): boolean {
  return getPasswordChecks(password).every((check) => check.met);
}

export function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  if (!password) return { score: 0, label: '', color: 'bg-gray-200' };

  const checks = getPasswordChecks(password);
  const metCount = checks.filter((c) => c.met).length;

  if (metCount <= 2) return { score: metCount, label: 'Weak', color: 'bg-red-500' };
  if (metCount <= 3) return { score: metCount, label: 'Fair', color: 'bg-orange-500' };
  if (metCount <= 4) return { score: metCount, label: 'Good', color: 'bg-yellow-500' };
  return { score: metCount, label: 'Strong', color: 'bg-green-500' };
}
